import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Search, Pencil, ExternalLink, CalendarClock } from 'lucide-react';
import {
  type ClinicSchedule, type ScheduleException, type BusyBlock,
} from '@/lib/schedule';

type Appt = {
  id: string;
  client_name: string;
  client_email: string | null;
  client_phone: string | null;
  starts_at: string;
  ends_at: string;
  duration_minutes: number | null;
  status: string;
  treatments: any;
  notes: string | null;
  google_event_id: string | null;
  google_event_link: string | null;
};

const STATUS_LABELS: Record<string, string> = {
  booked: 'מאושר',
  completed: 'הושלם',
  noshow: 'לא הגיע/ה',
  cancelled: 'בוטל',
};
const STATUSES = ['booked', 'completed', 'noshow', 'cancelled'];

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  booked: 'default',
  completed: 'secondary',
  noshow: 'outline',
  cancelled: 'destructive',
};

type RangeKey = 'today' | 'week' | 'month' | 'upcoming' | 'past' | 'all';

const RANGES: { key: RangeKey; label: string }[] = [
  { key: 'today', label: 'היום' },
  { key: 'week', label: 'השבוע' },
  { key: 'month', label: 'החודש' },
  { key: 'upcoming', label: 'קרובים' },
  { key: 'past', label: 'היסטוריה' },
  { key: 'all', label: 'הכל' },
];

function rangeBounds(key: RangeKey): { from?: Date; to?: Date; order: 'asc' | 'desc' } {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (key === 'today') {
    const end = new Date(startOfDay); end.setDate(end.getDate() + 1);
    return { from: startOfDay, to: end, order: 'asc' };
  }
  if (key === 'week') {
    const end = new Date(startOfDay); end.setDate(end.getDate() + 7);
    return { from: startOfDay, to: end, order: 'asc' };
  }
  if (key === 'month') {
    const end = new Date(startOfDay); end.setMonth(end.getMonth() + 1);
    return { from: startOfDay, to: end, order: 'asc' };
  }
  if (key === 'upcoming') return { order: 'asc' };
  if (key === 'past') return { to: now, order: 'desc' };
  return { order: 'desc' };
}

function fmtLocalInput(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fmtDateInput(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function groupByDay(items: Appt[]): { key: string; label: string; items: Appt[] }[] {
  const map = new Map<string, Appt[]>();
  for (const a of items) {
    const d = new Date(a.starts_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(a);
  }
  return Array.from(map.entries()).map(([key, items]) => ({
    key,
    label: new Date(items[0].starts_at).toLocaleDateString('he-IL', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    }),
    items,
  }));
}

type FormState = {
  startLocal: string; durationMinutes: number;
  name: string; phone: string; email: string; notes: string; status: string;
};

function toForm(a: Appt): FormState {
  const dt = new Date(a.starts_at);
  return {
    startLocal: fmtLocalInput(dt),
    durationMinutes: a.duration_minutes ?? Math.round((+new Date(a.ends_at) - +new Date(a.starts_at)) / 60000),
    name: a.client_name ?? '',
    phone: a.client_phone ?? '',
    email: a.client_email ?? '',
    notes: a.notes ?? '',
    status: a.status,
  };
}

// ---------------- Slot picker (re-uses public schedule resolver) ----------------

function toMin(t: string) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}
function fromMin(m: number) {
  const h = Math.floor(m / 60), mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function SlotPicker({
  durationMinutes, initialStartLocal, excludeRange, onPick, onCancel,
}: {
  durationMinutes: number;
  initialStartLocal: string;
  excludeRange?: { start: number; end: number };
  onPick: (newStartLocal: string) => void;
  onCancel: () => void;
}) {
  const initialDate = initialStartLocal.slice(0, 10) || fmtDateInput(new Date());
  const initialTime = initialStartLocal.slice(11, 16) || '10:00';
  const [dateStr, setDateStr] = useState(initialDate);
  const [timeStr, setTimeStr] = useState(initialTime);
  const [schedule, setSchedule] = useState<ClinicSchedule | null>(null);
  const [exceptions, setExceptions] = useState<ScheduleException[]>([]);
  const [busy, setBusy] = useState<BusyBlock[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const [{ data: schedRow }, { data: excRows }] = await Promise.all([
        supabase.from('clinic_schedule').select('*').limit(1).maybeSingle(),
        supabase.from('schedule_exceptions').select('date,closed,intervals,note'),
      ]);
      if (schedRow) {
        setSchedule({
          weekly: (schedRow as any).weekly,
          buffer_minutes: schedRow.buffer_minutes,
          slot_step_minutes: schedRow.slot_step_minutes,
          min_lead_minutes: schedRow.min_lead_minutes,
          horizon_days: schedRow.horizon_days,
          timezone: schedRow.timezone ?? 'Asia/Jerusalem',
        });
      }
      setExceptions(((excRows ?? []) as any[]) as ScheduleException[]);
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    const [y, m, d] = dateStr.split('-').map(Number);
    const dayStart = new Date(y, m - 1, d, 0, 0);
    const dayEnd = new Date(y, m - 1, d + 1, 0, 0);
    supabase.functions.invoke('calendar-busy', {
      body: { timeMin: dayStart.toISOString(), timeMax: dayEnd.toISOString() },
    }).then(({ data, error }) => {
      setLoading(false);
      if (error || (data as any)?.error) { setBusy([]); return; }
      const list = ((data as any)?.busy ?? []) as { start: string; end: string }[];
      let blocks = list.map((b) => ({ start: +new Date(b.start), end: +new Date(b.end) }));
      if (excludeRange) {
        blocks = blocks.filter((b) => !(b.start === excludeRange.start && b.end === excludeRange.end));
      }
      setBusy(blocks);
    });
  }, [dateStr, excludeRange?.start, excludeRange?.end]);

  // Working windows for the chosen day (intervals minus breaks), in local minutes.
  const dayInfo = useMemo(() => {
    if (!schedule) return { windows: [] as { start: number; end: number }[], closed: false };
    const [y, m, d] = dateStr.split('-').map(Number);
    const weekday = new Date(y, m - 1, d).getDay();
    const exc = exceptions.find((e) => e.date === dateStr);
    let intervals: { start: string; end: string }[];
    let breaks: { start: string; end: string }[] = [];
    if (exc) {
      if (exc.closed) return { windows: [], closed: true };
      intervals = exc.intervals ?? [];
    } else {
      const w = schedule.weekly[weekday];
      if (!w?.enabled) return { windows: [], closed: true };
      intervals = w.intervals ?? [];
      breaks = w.breaks ?? [];
    }
    const windows: { start: number; end: number }[] = [];
    for (const iv of intervals) {
      let segs = [{ start: toMin(iv.start), end: toMin(iv.end) }];
      for (const b of breaks) {
        const bs = toMin(b.start), be = toMin(b.end);
        const next: { start: number; end: number }[] = [];
        for (const s of segs) {
          if (be <= s.start || bs >= s.end) { next.push(s); continue; }
          if (bs > s.start) next.push({ start: s.start, end: bs });
          if (be < s.end) next.push({ start: be, end: s.end });
        }
        segs = next;
      }
      windows.push(...segs);
    }
    return { windows, closed: false };
  }, [schedule, exceptions, dateStr]);

  // Convert busy (UTC ms) to local minutes for display on this day.
  const busyDisplay = useMemo(() => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dayStartMs = +new Date(y, m - 1, d, 0, 0);
    const dayEndMs = +new Date(y, m - 1, d + 1, 0, 0);
    return busy
      .filter((b) => b.end > dayStartMs && b.start < dayEndMs)
      .map((b) => {
        const s = Math.max(0, Math.round((b.start - dayStartMs) / 60000));
        const e = Math.min(24 * 60, Math.round((b.end - dayStartMs) / 60000));
        return { s, e };
      })
      .sort((a, b) => a.s - b.s);
  }, [busy, dateStr]);

  // Validate currently chosen time.
  const validation = useMemo(() => {
    if (dayInfo.closed) return { ok: false, msg: 'יום סגור' };
    if (!timeStr) return { ok: false, msg: 'בחר/י שעה' };
    const start = toMin(timeStr);
    const buf = schedule?.buffer_minutes ?? 0;
    const end = start + durationMinutes;
    if (end > 24 * 60) return { ok: false, msg: 'חורג מסוף היום' };
    const inWindow = dayInfo.windows.some((w) => start >= w.start && end <= w.end);
    if (!inWindow) return { ok: false, msg: 'מחוץ לשעות הפעילות' };
    for (const b of busyDisplay) {
      // expand busy with buffer
      const bs = b.s - buf, be = b.e + buf;
      if (start < be && end > bs) return { ok: false, msg: 'חופף לפגישה קיימת' };
    }
    return { ok: true, msg: 'פנוי' };
  }, [timeStr, durationMinutes, dayInfo, busyDisplay, schedule?.buffer_minutes]);

  const workingLabel = dayInfo.closed
    ? 'יום סגור'
    : dayInfo.windows.length === 0
      ? '—'
      : dayInfo.windows.map((w) => `${fromMin(w.start)}–${fromMin(w.end)}`).join(', ');

  return (
    <div className="rounded-md border bg-background p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="date"
          value={dateStr}
          onChange={(e) => setDateStr(e.target.value)}
          className="w-auto"
        />
        <Input
          type="time"
          value={timeStr}
          step={60}
          onChange={(e) => setTimeStr(e.target.value)}
          className="w-auto"
        />
        <div className="text-xs text-muted-foreground">משך: {durationMinutes} דק׳</div>
      </div>

      <div className="text-xs text-muted-foreground">
        שעות פעילות: <span className="text-foreground">{workingLabel}</span>
      </div>

      <div>
        <div className="text-xs font-medium mb-1">פגישות קיימות ביום זה</div>
        {loading ? (
          <div className="text-xs text-muted-foreground">טוען…</div>
        ) : busyDisplay.length === 0 ? (
          <div className="text-xs text-muted-foreground">אין פגישות קיימות.</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {busyDisplay.map((b, i) => (
              <Badge key={i} variant="secondary" className="font-mono text-[11px]">
                {fromMin(b.s)}–{fromMin(b.e)}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className={`text-xs ${validation.ok ? 'text-emerald-600' : 'text-destructive'}`}>
        {validation.msg}
      </div>

      <div className="flex justify-start gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!validation.ok}
          onClick={() => onPick(`${dateStr}T${timeStr}`)}
        >
          אישור
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>ביטול</Button>
      </div>
    </div>
  );
}

// ---------------- Row ----------------

function ApptRow({
  a, expanded, onToggle, onSaved,
}: { a: Appt; expanded: boolean; onToggle: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<FormState>(() => toForm(a));
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => { setForm(toForm(a)); setShowPicker(false); }, [a, expanded]);

  const start = new Date(a.starts_at);
  const end = new Date(a.ends_at);
  const dur = Math.round((+end - +start) / 60000);

  const formStart = form.startLocal ? new Date(form.startLocal) : start;
  const formStartLabel = formStart.toLocaleString('he-IL', {
    weekday: 'short', day: 'numeric', month: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  const save = async () => {
    setSaving(true);
    const originalLocal = fmtLocalInput(new Date(a.starts_at));
    const body: any = {
      appointmentId: a.id,
      name: form.name,
      phone: form.phone,
      email: form.email,
      notes: form.notes,
      durationMinutes: Number(form.durationMinutes) || 60,
      status: form.status,
    };
    if (form.startLocal && form.startLocal !== originalLocal) body.startLocal = form.startLocal;
    const { data, error } = await supabase.functions.invoke('update-appointment', { body });
    setSaving(false);
    if (error || (data as any)?.error) {
      const code = (data as any)?.error ?? error?.message ?? 'שגיאה';
      const map: Record<string, string> = {
        slot_unavailable: 'הזמן תפוס ביומן',
        outside_window: 'מחוץ לשעות הפעילות',
        closed_day: 'יום סגור',
        event_update_failed: 'עדכון יומן נכשל',
        calendar_unavailable: 'יומן לא זמין',
      };
      toast.error(map[code] ?? code);
      return;
    }
    toast.success('נשמר');
    onSaved();
  };

  return (
    <Card className="p-0 relative overflow-hidden">
      <span className="absolute -start-[1.4rem] top-4 w-3 h-3 rounded-full bg-primary border-2 border-background" />
      <div className="p-3 grid grid-cols-12 gap-3 items-center">
        {/* Time block — start side (visual right in RTL) */}
        <div className="col-span-12 md:col-span-2 text-sm">
          <div className="font-semibold text-base">
            {start.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div className="text-xs text-muted-foreground">{dur} דק׳</div>
        </div>

        {/* Name + contact */}
        <div className="col-span-12 md:col-span-4 text-sm min-w-0">
          <div className="font-medium truncate">{a.client_name}</div>
          <div className="text-xs text-muted-foreground truncate">
            {a.client_email ?? '—'}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {a.client_phone ?? '—'}
          </div>
        </div>

        {/* Treatments */}
        <div className="col-span-12 md:col-span-3 text-xs text-muted-foreground min-w-0">
          {Array.isArray(a.treatments) && a.treatments.length > 0
            ? a.treatments.join(' · ')
            : '—'}
        </div>

        {/* Status + edit — end side (visual left in RTL) */}
        <div className="col-span-12 md:col-span-3 flex items-center justify-end gap-2">
          <Badge variant={STATUS_VARIANT[a.status] ?? 'outline'}>
            {STATUS_LABELS[a.status] ?? a.status}
          </Badge>
          <Button size="sm" variant={expanded ? 'default' : 'outline'} onClick={onToggle}>
            <Pencil className="w-3.5 h-3.5 me-1" />
            {expanded ? 'סגירה' : 'ערוך'}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t p-4 grid gap-3 bg-muted/20">
          {/* Date / time — shown as an edit button that opens the slot picker */}
          <div>
            <Label>תאריך ושעה</Label>
            {!showPicker ? (
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2 rounded-md border bg-background text-sm">
                  {formStartLabel}
                </div>
                <Button type="button" size="sm" variant="outline" onClick={() => setShowPicker(true)}>
                  <CalendarClock className="w-3.5 h-3.5 me-1" />
                  ערוך תאריך ושעה
                </Button>
              </div>
            ) : (
              <SlotPicker
                durationMinutes={Number(form.durationMinutes) || 60}
                initialStartLocal={form.startLocal}
                excludeRange={{ start: +new Date(a.starts_at), end: +new Date(a.ends_at) }}
                onPick={(v) => { setForm((f) => ({ ...f, startLocal: v })); setShowPicker(false); }}
                onCancel={() => setShowPicker(false)}
              />
            )}
            <div className="text-xs text-muted-foreground mt-1">
              בחר/י תאריך וזמן חופשי — הפגישות התפוסות ביום מסומנות.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>משך (דק׳)</Label>
              <Input
                type="number" min={5} max={600}
                value={form.durationMinutes}
                onChange={(e) => setForm((f) => ({ ...f, durationMinutes: Number(e.target.value) }))}
              />
            </div>
            <div>
              <Label>סטטוס</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>שם</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>טלפון</Label>
              <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
            </div>
            <div>
              <Label>אימייל</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label>הערות</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="flex items-center justify-between gap-2 pt-1">
            {a.google_event_link ? (
              <a href={a.google_event_link} target="_blank" rel="noopener noreferrer"
                className="text-xs underline inline-flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> פתח ביומן Google
              </a>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onToggle} disabled={saving}>סגירה</Button>
              <Button size="sm" onClick={save} disabled={saving}>{saving ? 'שומר…' : 'שמירה'}</Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function AdminAppointments() {
  const [items, setItems] = useState<Appt[]>([]);
  const [range, setRange] = useState<RangeKey>('upcoming');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const b = rangeBounds(range);
    let q = supabase.from('appointments').select('*').order('starts_at', { ascending: b.order === 'asc' });
    if (b.from) q = q.gte('starts_at', b.from.toISOString());
    if (b.to) q = q.lt('starts_at', b.to.toISOString());
    const { data, error } = await q;
    if (error) toast.error(error.message);
    setItems(((data as any) ?? []) as Appt[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, [range]);

  const { nextAppt, restGroups, filteredCount } = useMemo(() => {
    const s = search.trim().toLowerCase();
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    const filtered = items.filter((a) => {
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (range === 'upcoming') {
        const isActive = !['cancelled', 'completed', 'no_show'].includes(a.status);
        const isFuture = +new Date(a.starts_at) >= +startOfToday;
        if (!isActive && !isFuture) return false;
      }
      if (!s) return true;
      const hay = [
        a.client_name, a.client_email ?? '', a.client_phone ?? '',
        Array.isArray(a.treatments) ? a.treatments.join(' ') : '',
        a.notes ?? '',
      ].join(' ').toLowerCase();
      return hay.includes(s);
    });
    if (range === 'upcoming' && filtered.length > 0) {
      const [first, ...rest] = filtered;
      return { nextAppt: first, restGroups: groupByDay(rest), filteredCount: filtered.length };
    }
    return { nextAppt: null as Appt | null, restGroups: groupByDay(filtered), filteredCount: filtered.length };
  }, [items, statusFilter, search, range]);

  // auto-expand the nearest upcoming appointment on first load
  useEffect(() => {
    if (range === 'upcoming' && nextAppt && expandedId === null) {
      setExpandedId(nextAppt.id);
    }
  }, [range, nextAppt?.id]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-semibold">תורים</h1>
        <div className="flex gap-2 flex-wrap">
          {RANGES.map((r) => (
            <Button key={r.key} variant={range === r.key ? 'default' : 'outline'} size="sm" onClick={() => setRange(r.key)}>
              {r.label}
            </Button>
          ))}
        </div>
      </div>

      <Card className="p-3 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="absolute top-1/2 -translate-y-1/2 end-3 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="חיפוש לפי שם, אימייל, טלפון, טיפול…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pe-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">כל הסטטוסים</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground whitespace-nowrap">{filteredCount} תורים</div>
      </Card>

      {loading && <div className="text-sm text-muted-foreground">טוען…</div>}
      {!loading && filteredCount === 0 && (
        <Card className="p-6 text-sm text-muted-foreground text-center">אין תורים בטווח זה.</Card>
      )}

      {range === 'upcoming' && nextAppt && !loading && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="default">הבא</Badge>
            <span className="text-xs text-muted-foreground">{new Date(nextAppt.starts_at).toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
          </div>
          <ApptRow
            a={nextAppt}
            expanded={expandedId === nextAppt.id}
            onToggle={() => setExpandedId((cur) => (cur === nextAppt.id ? null : nextAppt.id))}
            onSaved={() => { setExpandedId(null); load(); }}
          />
        </div>
      )}

      <div className="space-y-6">
        {restGroups.map((g) => (
          <div key={g.key}>
            <div className="sticky top-0 z-10 bg-background/80 backdrop-blur py-2 mb-2 border-b">
              <div className="text-sm font-semibold">{g.label}</div>
              <div className="text-xs text-muted-foreground">{g.items.length} תורים</div>
            </div>
            <div className="relative ps-4 border-s-2 border-border space-y-3">
              {g.items.map((a) => (
                <ApptRow
                  key={a.id}
                  a={a}
                  expanded={expandedId === a.id}
                  onToggle={() => setExpandedId((cur) => (cur === a.id ? null : a.id))}
                  onSaved={() => { setExpandedId(null); load(); }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
